import { createSlice, PayloadAction, UnknownAction } from "@reduxjs/toolkit";

export type uiState = {
  windowWidth: number;
  windowHeight: number;
  isShown: boolean;
  isError: boolean;
  status: string;
  message: string;
  deviceCalendar: boolean;
  addEvent: boolean;
  isRefreshing: boolean;
  isFormDirty: boolean;
  canSaveForm: boolean;
  pendingFormAction: UnknownAction | null;
  saveBeforeLeave: boolean;
};

const initialState: uiState = {
  windowWidth: window.innerWidth,
  windowHeight: window.innerHeight,
  isShown: false,
  isError: false,
  status: "idle",
  message: "",
  deviceCalendar: false,
  addEvent: false,
  isRefreshing: false,
  isFormDirty: false,
  // Whether Save has anything to do; read by the phone header's Save too.
  canSaveForm: false,
  // What the user was trying to do when the unsaved changes warning came up.
  pendingFormAction: null,
  // Set by Save in that warning: the form saves, then carries on with
  // pendingFormAction if the save went through.
  saveBeforeLeave: false,
};

const uiSlice = createSlice({
  name: "ui",
  initialState: initialState,
  reducers: {
    resize(
      state,
      action: PayloadAction<{
        width: number;
        height: number;
      }>
    ) {
      state.windowWidth = action.payload.width;
      state.windowHeight = action.payload.height;
    },
    error(
      state,
      action: PayloadAction<{
        message: string;
      }>
    ) {
      state.isShown = true;
      state.isError = true;
      state.status = "error";
      state.message = action.payload.message;
    },
    success(
      state,
      action: PayloadAction<{
        message: string;
      }>
    ) {
      state.isShown = true;
      state.isError = false;
      state.status = "success";
      state.message = action.payload.message;
    },
    loading(
      state,
      action: PayloadAction<{
        message: string;
      }>
    ) {
      state.isShown = true;
      state.isError = false;
      state.status = "loading";
      state.message = action.payload.message;
    },
    hide(state) {
      state.isShown = false;
    },
    // Background sync is deliberately separate from `loading` - that one puts
    // a blocking modal over the app, which would be obnoxious for a refresh
    // the user didn't ask for.
    refreshStart(state) {
      state.isRefreshing = true;
    },
    refreshEnd(state) {
      state.isRefreshing = false;
    },
    unset(state) {
      state.isShown = false;
      state.isError = false;
      state.status = "idle";
      state.message = "";
    },
    showDeviceCalendar(state) {
      state.deviceCalendar = true;
    },
    hideDeviceCalendar(state) {
      state.deviceCalendar = false;
    },
    showAddForm(state) {
      state.addEvent = true;
    },
    hideEventForm(state) {
      state.addEvent = false;
    },
    setFormDirty(state, action: PayloadAction<boolean>) {
      state.isFormDirty = action.payload;
    },
    setCanSaveForm(state, action: PayloadAction<boolean>) {
      state.canSaveForm = action.payload;
    },
    confirmLeaveForm(state, action: PayloadAction<UnknownAction>) {
      state.pendingFormAction = action.payload;
    },
    cancelLeaveForm(state) {
      state.pendingFormAction = null;
      state.saveBeforeLeave = false;
    },
    saveBeforeLeave(state) {
      state.saveBeforeLeave = true;
    },
  },
});

export default uiSlice.reducer;
export const UIActions = uiSlice.actions;
